/**
 * CME MES 2m candles for the MES candle overlay on MNQ/NQ charts (issue #620).
 * Runs only while some chart shows the overlay (`start` / `stop`, ref counted);
 * nothing is fetched or listened to otherwise.
 *
 * - History: REST `GET {api}/historical/{MES contract}?count=N` (the engine's
 *   last N 2m candles; needs the watcher key, e.g. `MESZ6.CME`, resolved from
 *   the engine's streamed symbols by `resolveMesSymbol`). N covers the chart's
 *   candles plus the ratio's 6h warm-up and grows when the chart lazy-loads.
 * - Live: the engine broadcasts `market-data` {symbol, candle} for EVERY
 *   streamed symbol to every client, so no subscribe is needed (a subscribe
 *   would also replay historical-data / price-levels into the chart's own
 *   handlers). Ticks are coalesced and merged at most every MES_FEED_FLUSH_MS.
 * - Reconnect: the bars that closed while the socket was down are refetched.
 *
 * Bars only; the ratio is computed in ChartView from the bars on hand on every
 * update (utils/mesCandles) and never stored.
 */
import { create } from 'zustand';
import { wsService } from '../services/WebSocketService';
import { getApiBaseUrl } from '../config/ports';
import { MesBar, mergeMesBars, parseMesCandles } from '../utils/mesCandles';

export const MES_FEED_FLUSH_MS = 500;
/** 6h of 2m bars: the ratio's warm-up before the chart's first candle. */
export const MES_WARMUP_BARS = 180;
/** Cap on one history request (~12 sessions of 2m bars). */
export const MES_FEED_MAX_COUNT = 8640;
/** Bars refetched after a reconnect (12h). */
const RECONNECT_REFETCH = 360;

export type MesFeedStatus = 'idle' | 'loading' | 'ready' | 'error';

interface MesCandleFeedState {
  symbol: string | null;
  bars: Map<number, MesBar>;
  /** Largest count fetched for `symbol`. */
  loadedCount: number;
  status: MesFeedStatus;
  error: string | null;

  /** A chart starts showing the overlay; `count` = candles it needs. */
  start: (symbol: string, count: number) => void;
  /** The chart wants more history (lazy load); no new user. */
  want: (count: number) => void;
  stop: () => void;
  applyLive: (payload: unknown) => void;
  flush: () => void;
  fetchHistory: (count: number) => Promise<void>;
}

let users = 0;
let handlersRegistered = false;
let retryTimer: ReturnType<typeof setInterval> | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let pending = new Map<number, MesBar>();
/** Bumped on every reset (stop, symbol change): responses from an older epoch are dropped. */
let epoch = 0;
/** Bumped on every live flush. */
let liveGen = 0;
/** Bar open -> liveGen of the flush that last wrote it. History fetched before that flush must not overwrite it. */
let liveTouched = new Map<number, number>();
/** Counts of history requests in flight. */
const inflightCounts: number[] = [];
/** Largest count that came back successfully this epoch. */
let succeededCount = 0;

function resetFeedState(): void {
  epoch += 1;
  pending = new Map();
  liveTouched = new Map();
  inflightCounts.length = 0;
  succeededCount = 0;
}

const onMarketData = (payload: unknown): void => useMesCandleFeedStore.getState().applyLive(payload);

const onConnect = (): void => {
  const s = useMesCandleFeedStore.getState();
  if (s.symbol && s.loadedCount > 0) void s.fetchHistory(Math.min(s.loadedCount, RECONNECT_REFETCH));
};

function tryRegister(): boolean {
  if (handlersRegistered) return true;
  if (!wsService.isConnected()) return false;
  wsService.on('market-data', onMarketData);
  wsService.on('connect', onConnect);
  handlersRegistered = true;
  // After forceReconnect the new socket's 'connect' can fire before we get
  // here (we register only once it is connected): refetch the gap now.
  onConnect();
  return true;
}

function startRegistering(): void {
  if (tryRegister() || retryTimer !== null) return;
  retryTimer = setInterval(() => {
    if (users === 0 || tryRegister()) {
      if (retryTimer !== null) clearInterval(retryTimer);
      retryTimer = null;
    }
  }, 2000);
}

const clampCount = (n: number): number => Math.max(1, Math.min(MES_FEED_MAX_COUNT, Math.ceil(n)));

export const useMesCandleFeedStore = create<MesCandleFeedState>((set, get) => ({
  symbol: null,
  bars: new Map(),
  loadedCount: 0,
  status: 'idle',
  error: null,

  start: (symbol, count) => {
    users += 1;
    if (get().symbol !== symbol) {
      resetFeedState();
      set({ symbol, bars: new Map(), loadedCount: 0, status: 'idle', error: null });
    }
    startRegistering();
    get().want(count);
  },

  want: (count) => {
    const n = clampCount(count + MES_WARMUP_BARS);
    if (users > 0 && get().symbol && n > get().loadedCount) void get().fetchHistory(n);
  },

  stop: () => {
    users = Math.max(0, users - 1);
    if (users > 0) return;
    if (retryTimer !== null) clearInterval(retryTimer);
    retryTimer = null;
    if (flushTimer !== null) clearTimeout(flushTimer);
    flushTimer = null;
    resetFeedState();
    if (handlersRegistered) {
      wsService.off('market-data', onMarketData);
      wsService.off('connect', onConnect);
      handlersRegistered = false;
    }
    set({ symbol: null, bars: new Map(), loadedCount: 0, status: 'idle', error: null });
  },

  applyLive: (payload) => {
    const p = payload as { symbol?: unknown; candle?: unknown } | null;
    const sym = get().symbol;
    if (!p || !sym || p.symbol !== sym) return;
    const [bar] = parseMesCandles([p.candle]);
    if (!bar) return;
    pending.set(bar.t, bar);
    if (flushTimer === null) flushTimer = setTimeout(() => get().flush(), MES_FEED_FLUSH_MS);
  },

  flush: () => {
    if (flushTimer !== null) clearTimeout(flushTimer);
    flushTimer = null;
    if (pending.size === 0) return;
    const incoming = [...pending.values()];
    pending = new Map();
    liveGen += 1;
    for (const b of incoming) liveTouched.set(b.t, liveGen);
    const next = mergeMesBars(get().bars, incoming);
    if (next) set({ bars: next });
  },

  fetchHistory: async (count) => {
    const symbol = get().symbol;
    if (!symbol) return;
    const n = clampCount(count);
    const myEpoch = epoch;
    const startGen = liveGen;
    inflightCounts.push(n);
    const done = () => {
      const i = inflightCounts.indexOf(n);
      if (i >= 0) inflightCounts.splice(i, 1);
    };
    set({ status: 'loading', loadedCount: Math.max(get().loadedCount, n) });
    try {
      const res = await fetch(`${getApiBaseUrl()}/historical/${encodeURIComponent(symbol)}?count=${n}`);
      const body = await res.json().catch(() => null);
      if (!res.ok || !body || typeof body.error === 'string') throw new Error(body?.error ?? `HTTP ${res.status}`);
      if (epoch !== myEpoch) return; // stopped or rolled meanwhile
      done();
      succeededCount = Math.max(succeededCount, n);
      // A live flush after this request started holds newer data for its bars.
      const fresh = parseMesCandles(body.candles).filter((b) => (liveTouched.get(b.t) ?? -1) <= startGen);
      const next = mergeMesBars(get().bars, fresh);
      set({ bars: next ?? get().bars, status: 'ready', error: null });
    } catch (e) {
      if (epoch !== myEpoch) return;
      done();
      // Allow a later want() to retry this count, but keep what a concurrent
      // larger request already covers (succeeded or still in flight).
      const covered = Math.max(succeededCount, ...inflightCounts, 0);
      set({ status: 'error', error: e instanceof Error ? e.message : String(e), loadedCount: covered });
    }
  },
}));

/**
 * WebSocketService.forceReconnect() replaces the socket object, dropping
 * every listener: forget ours before, register on the new socket after.
 */
export function resetMesCandleFeedSocketHandlers(): void {
  handlersRegistered = false;
}

export function initMesCandleFeedSocketHandlers(): void {
  if (users > 0) startRegistering();
}
